import { toast } from '@heroui/react';
import { useCallback, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import {
  Button,
  Description,
  FieldError,
  Input,
  Label,
  ListBox,
  Modal,
  Select,
  TextField,
} from '@heroui/react';
import { trpc } from '@/lib/trpc';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

const USER_STATUSES = ['ACTIVE', 'LOCKED', 'DISABLED'] as const;

const USER_STATUS_LABELS: Record<(typeof USER_STATUSES)[number], string> = {
  ACTIVE: '激活',
  LOCKED: '锁定',
  DISABLED: '禁用',
};

const passwordSchema = z
  .string()
  .min(6, '密码长度至少6位')
  .regex(/[a-z]/, '须包含小写字母')
  .regex(/[A-Z]/, '须包含大写字母')
  .regex(/\d/, '须包含数字')
  .regex(/[^a-zA-Z0-9]/, '须包含特殊符号');

const userBaseSchema = z.object({
  username: z
    .string()
    .min(3, '用户名至少3个字符')
    .max(32, '用户名长度不能超过32字符')
    .regex(/^[a-zA-Z][a-zA-Z0-9_]*$/, '用户名须以字母开头，只能包含字母、数字和下划线'),
  nickname: z.string().max(32, '昵称长度不能超过32字符'),
  realname: z.string().max(64, '真实姓名长度不能超过64字符'),
  email: z
    .string()
    .max(64, '邮箱长度不能超过64字符')
    .refine((v) => v === '' || z.email().safeParse(v).success, '邮箱格式不正确'),
  mobile: z
    .string()
    .max(11, '手机号长度不能超过11位')
    .refine((v) => v === '' || /^1[3-9]\d{9}$/.test(v), '手机号格式不正确'),
  status: z.enum(USER_STATUSES),
});

const createUserSchema = userBaseSchema.extend({
  password: passwordSchema,
});

const updateUserSchema = userBaseSchema.extend({
  password: z.string().optional(),
});

type UserFormValues = z.infer<typeof createUserSchema>;

export type UserFormItem = {
  id?: string;
  username?: string | null;
  nickname?: string | null;
  realname?: string | null;
  email?: string | null;
  mobile?: string | null;
  status?: string | null;
  avatarUrl?: string | null;
};

interface UserEditorModalProps {
  open: boolean;
  user?: UserFormItem | null;
  onCancel: () => void;
  onSuccess: () => void;
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function buildDefaultValues(user?: UserFormItem | null): UserFormValues {
  if (user) {
    return {
      username: user.username || '',
      nickname: user.nickname || '',
      realname: user.realname || '',
      email: user.email || '',
      mobile: user.mobile || '',
      status: (USER_STATUSES as readonly string[]).includes(user.status ?? '')
        ? (user.status as (typeof USER_STATUSES)[number])
        : 'ACTIVE',
      password: '',
    };
  }

  return {
    username: '',
    nickname: '',
    realname: '',
    email: '',
    mobile: '',
    status: 'ACTIVE',
    password: '',
  };
}

function toUserPayload(values: UserFormValues) {
  return {
    username: values.username.trim(),
    nickname: emptyToNull(values.nickname),
    realname: emptyToNull(values.realname),
    email: emptyToNull(values.email),
    mobile: emptyToNull(values.mobile),
    status: values.status,
  };
}

export function UserEditorModal({ open, user, onCancel, onSuccess }: UserEditorModalProps) {
  const utils = trpc.useUtils();
  const isEdit = Boolean(user?.id);
  const isEditRef = useRef(isEdit);
  isEditRef.current = isEdit;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  const handleModalOpenChange = useCallback((next: boolean) => {
    if (!next) onCancelRef.current();
  }, []);

  const form = useForm<UserFormValues>({
    resolver: async (values, context, options) => {
      const schema = isEditRef.current ? updateUserSchema : createUserSchema;
      return zodResolver(schema)(values, context, options);
    },
    defaultValues: buildDefaultValues(null),
    mode: 'onChange',
  });
  const { reset } = form;

  const createUserMutation = trpc.users.create.useMutation({
    onError: (error) => {
      toast.danger('创建失败', { description: error.message });
    },
  });

  const updateUserMutation = trpc.users.update.useMutation({
    onSuccess: () => {
      toast.success('用户更新成功');
      form.reset();
      onSuccess();
      void utils.users.list.invalidate();
    },
    onError: (error) => {
      toast.danger('更新失败', { description: error.message });
    },
  });

  const resetPasswordMutation = trpc.users.resetPassword.useMutation({
    onError: (error) => {
      toast.danger('用户已创建，但设置密码失败', { description: error.message });
    },
  });

  useEffect(() => {
    if (!open) {
      reset(buildDefaultValues(null));
      return;
    }
    reset(buildDefaultValues(isEdit ? user : null));
  }, [open, isEdit, user, reset]);

  const onSubmit = async (values: UserFormValues) => {
    const payload = toUserPayload(values);

    if (isEdit && user?.id) {
      await updateUserMutation.mutateAsync({
        id: user.id,
        data: payload,
      });
      return;
    }

    const created = await createUserMutation.mutateAsync(payload);
    try {
      await resetPasswordMutation.mutateAsync({
        id: created.id,
        newPassword: values.password,
      });
      toast.success('用户创建成功');
    } catch {
      /* onError already toasted */
    }
    form.reset();
    onSuccess();
    void utils.users.list.invalidate();
  };

  const isLoading =
    createUserMutation.isPending || updateUserMutation.isPending || resetPasswordMutation.isPending;
  const errors = form.formState.errors;

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="lg" className="max-w-2xl">
          <Modal.Dialog
            key={open ? (isEdit && user ? `edit-${String(user.id ?? '')}` : 'create') : 'closed'}
          >
            <Modal.CloseTrigger />
            <Modal.Header className="gap-1">
              <Modal.Heading className="text-xl font-medium">
                {isEdit ? '编辑用户' : '创建用户'}
              </Modal.Heading>
              <Description className="text-sm">
                {isEdit ? '修改用户信息' : '填写用户信息以创建新用户'}
              </Description>
            </Modal.Header>
            <Modal.Body className="gap-4 p-2">
              <form
                id="editor-user-form"
                onSubmit={form.handleSubmit(onSubmit as any)}
                className="flex min-w-0 flex-col gap-4"
              >
                <Controller
                  control={form.control}
                  name="username"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.username}>
                      <Label className="text-sm font-medium">
                        用户名 <span className="text-danger">*</span>
                      </Label>
                      <Input
                        maxLength={32}
                        placeholder="请输入用户名"
                        variant="secondary"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      <Description className="text-xs">
                        以字母开头，可含字母、数字与下划线，至少 3 个字符
                      </Description>
                      {errors.username ? <FieldError>{errors.username.message}</FieldError> : null}
                    </TextField>
                  )}
                />

                {isEdit ? null : (
                  <Controller
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <TextField className="w-full" isInvalid={!!errors.password}>
                        <Label className="text-sm font-medium">
                          初始密码 <span className="text-danger">*</span>
                        </Label>
                        <Input
                          autoComplete="new-password"
                          placeholder="请输入初始密码"
                          type="password"
                          variant="secondary"
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                        />
                        <Description className="text-xs">
                          不少于 6 位，须包含大小写字母、数字与特殊符号
                        </Description>
                        {errors.password ? <FieldError>{errors.password.message}</FieldError> : null}
                      </TextField>
                    )}
                  />
                )}

                <Controller
                  control={form.control}
                  name="nickname"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.nickname}>
                      <Label className="text-sm font-medium">昵称</Label>
                      <Input
                        maxLength={32}
                        placeholder="请输入昵称"
                        variant="secondary"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      {errors.nickname ? <FieldError>{errors.nickname.message}</FieldError> : null}
                    </TextField>
                  )}
                />

                <Controller
                  control={form.control}
                  name="realname"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.realname}>
                      <Label className="text-sm font-medium">真实姓名</Label>
                      <Input
                        maxLength={64}
                        placeholder="请输入真实姓名"
                        variant="secondary"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      {errors.realname ? <FieldError>{errors.realname.message}</FieldError> : null}
                    </TextField>
                  )}
                />

                <Controller
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.email}>
                      <Label className="text-sm font-medium">邮箱</Label>
                      <Input
                        maxLength={64}
                        placeholder="请输入邮箱"
                        type="email"
                        variant="secondary"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      {errors.email ? <FieldError>{errors.email.message}</FieldError> : null}
                    </TextField>
                  )}
                />

                <Controller
                  control={form.control}
                  name="mobile"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.mobile}>
                      <Label className="text-sm font-medium">手机号</Label>
                      <Input
                        maxLength={11}
                        placeholder="请输入手机号"
                        variant="secondary"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      {errors.mobile ? <FieldError>{errors.mobile.message}</FieldError> : null}
                    </TextField>
                  )}
                />

                <Controller
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <Select
                      className="w-full"
                      isInvalid={!!errors.status}
                      variant="secondary"
                      value={field.value}
                      onChange={(key) => {
                        if (key != null) field.onChange(String(key));
                      }}
                    >
                      <Label className="text-sm font-medium">
                        状态 <span className="text-danger">*</span>
                      </Label>
                      <Select.Trigger>
                        <Select.Value />
                        <Select.Indicator />
                      </Select.Trigger>
                      <Select.Popover>
                        <ListBox>
                          {USER_STATUSES.map((status) => (
                            <ListBox.Item key={status} id={status} textValue={USER_STATUS_LABELS[status]}>
                              {USER_STATUS_LABELS[status]}
                            </ListBox.Item>
                          ))}
                        </ListBox>
                      </Select.Popover>
                      {errors.status ? <FieldError>{errors.status.message}</FieldError> : null}
                    </Select>
                  )}
                />
              </form>
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="ghost" onPress={onCancel}>
                取消
              </Button>
              <Button
                isDisabled={isLoading}
                variant="primary"
                onPress={() => void form.handleSubmit(onSubmit as any)()}
              >
                {isLoading ? '提交中…' : isEdit ? '更新' : '创建'}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
